import React, { createContext, useContext, useEffect, useState } from 'react';
import { CaseDataResponse } from '../services/api/types';
import {
  ServerDataPopType,
  useApi,
  useCaseId,
  useDataPop,
  useDialog,
  useLocalStorage,
} from '../hooks';
import {
  CALL_TERMINATED_TIMEOUT,
  STORAGE_CURRENT_CASE_ID,
  STORAGE_KEY_CALL911STATUS,
  STORAGE_PREVIOUS_CASE_ID,
} from '../constants';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { COLORS, SIZES } from '../styles/theme';

const StyledPrimaryButton = styled(Button)(({}) => ({
  color: COLORS.WHITE,
  backgroundColor: COLORS.PRIMARY_BLUE,
  minWidth: SIZES.MIN_BUTTON_WIDTH,
  float: 'right',
  marginRight: '10px',
  '&:hover': {
    backgroundColor: COLORS.PRIMARY_BLUE,
  },
}));

export interface CaseContextProps {
  call911Status: string;
  setCall911Status: React.Dispatch<React.SetStateAction<string>>;
  caseData: CaseDataResponse | null;
  setCaseData: React.Dispatch<React.SetStateAction<CaseDataResponse | null>>;
  isLocationValidated: boolean;
  setIsLocationValidated: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenExtensionDialog: boolean;
  setIsOpenExtensionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  closeCase: () => void;
  caseTerminated: boolean | null;
  saveCall911Status: (call911StatusInput: string) => void;
}

export const CaseContext = createContext<CaseContextProps | null>(null);

export const CaseProvider = ({ children }: { children: React.ReactNode }) => {
  const { messages } = useDataPop();
  const { api } = useApi();
  const location = useLocation();
  const { closeDialog, openDialog } = useDialog();
  const { withLocalStorage, removeLocalStorage } = useLocalStorage();
  const { setActiveCaseId } = useCaseId();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [call911Status, setCall911Status] = useState<string>('INACTIVE');
  const [caseData, setCaseData] = useState<CaseDataResponse | null>(null);
  const [caseTerminated, setCaseTerminated] = useState<boolean | null>(null);
  const [isLocationValidated, setIsLocationValidated] =
    useState<boolean>(false);
  const [isOpenExtensionDialog, setIsOpenExtensionDialog] =
    useState<boolean>(false);

  useEffect(() => {
    const data: CaseDataResponse = {
      callDetails: {},
      vehicle: {},
      customer: {},
      location: {},
      casesteps: [],
      status: 0,
      response_message: '',
    };
    setCaseData(data);
    setLoading(false);
  }, []);

  const closeCase = () => {
    setCaseData(null);
    removeLocalStorage([STORAGE_CURRENT_CASE_ID, STORAGE_KEY_CALL911STATUS]);
    setActiveCaseId('');
  };

  const onDialogConfirmOk = async (): Promise<boolean> => {
    closeDialog();
    return true;
  };

  useEffect(() => {
    const call911StatusInput = withLocalStorage(STORAGE_KEY_CALL911STATUS);
    setCall911Status(call911StatusInput ? call911StatusInput : call911Status);
  }, [call911Status, withLocalStorage]);

  const saveCall911Status = (call911StatusInput: string) => {
    setCall911Status(call911StatusInput);
    withLocalStorage(STORAGE_KEY_CALL911STATUS, call911StatusInput);
  };

  const handleIncomingCall = (jsonData: any) => {
    closeDialog();
    const caseDetails = JSON.parse(JSON.parse(jsonData.message));
    const currentCaseData = api.makeGetCaseDataResponse(caseDetails);
    const localCurrentCaseId = withLocalStorage(STORAGE_CURRENT_CASE_ID);
    const currentCaseId = currentCaseData?.callDetails?.caseId;
    if (localCurrentCaseId !== currentCaseId) {
      setCaseData(api.makeGetCaseDataResponse(caseDetails));
      saveCall911Status('INACTIVE');
      setIsLocationValidated(false);
      setIsOpenExtensionDialog(false);
      setCaseTerminated(false);
    }
  };

  const handleCallterminated = (jsonData: any) => {
    const caseDetails = JSON.parse(jsonData.message);
    if (
      withLocalStorage(STORAGE_CURRENT_CASE_ID) === caseDetails?.case_id &&
      caseTerminated !== true
    ) {
      setCaseTerminated(true);
      let dialogMessage = '';
      if (location.pathname === '/app/activecase') {
        dialogMessage = `Connected Vehicle has detected that the call with vehicle is terminated. The page will be automatically refreshed in 90 seconds.`;
      } else {
        dialogMessage = `Connected Vehicle has detected that the call with vehicle is terminated. The Active Case details would be refreshed after 90 seconds.`;
      }

      openDialog({
        children: (
          <>
            <p>{dialogMessage}</p>
            <StyledPrimaryButton
              onClick={onDialogConfirmOk}
              color="primary"
              data-testid="cancelButton"
            >
              OK
            </StyledPrimaryButton>
          </>
        ),
        id: 'call-terminated-dialog',
        title: { show: true, text: 'Call Terminated' },
        btnCancel: { show: false, text: '' },
        btnOk: { show: false, text: 'OK' },
      });

      setTimeout(() => {
        closeCase();
        closeDialog();

        if (
          withLocalStorage(STORAGE_PREVIOUS_CASE_ID) === caseDetails?.case_id &&
          withLocalStorage(STORAGE_CURRENT_CASE_ID) === undefined &&
          (location.pathname === '/app/activecase' ||
            location.pathname === '/app/settings')
        ) {
          navigate(`/app/landing`);
        } else {
          withLocalStorage(STORAGE_PREVIOUS_CASE_ID, caseDetails?.case_id);
        }
      }, CALL_TERMINATED_TIMEOUT);
    }
  };

  useEffect(() => {
    if (messages !== undefined && messages.length > 0) {
      const jsonData = messages.slice(-1)[0];
      if (jsonData.type === ServerDataPopType.datapop) {
        handleIncomingCall(jsonData);
      } else if (jsonData.type === ServerDataPopType.callterminated) {
        handleCallterminated(jsonData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  if (loading) {
    return null;
  }
  return (
    <CaseContext.Provider
      value={{
        caseData,
        setCaseData,
        call911Status,
        setCall911Status,
        isLocationValidated,
        setIsLocationValidated,
        isOpenExtensionDialog,
        setIsOpenExtensionDialog,
        closeCase,
        caseTerminated,
        saveCall911Status,
      }}
    >
      {children}
    </CaseContext.Provider>
  );
};

export function useCase(): CaseContextProps {
  const context = useContext(CaseContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useCase outside of CaseProvider');
}
